<template>
  <div class="examinations">
    <div class="area buttons">
      <base-button-register
        :text="'検査結果PDF登録'"
        @click="openExaminationPdfFilePopup"
      />
    </div>
    <div class="area owner-patient">
      <owner-patient-detail
        :owner="getOwner(ownerId)"
        :patient="getPatient(patientId)"
      />
    </div>
    <div class="area search">
      <search-area
        :period-flg="true"
        :period-label="'期間'"
        :placeholder="'検査名、項目区分名、項目名、コメント、ファイル名'"
        :placeholder-small-font="true"
        :radio-button-data="displayTypes"
        :radio-button-label="'表示'"
        :radio-button-value="displayType"
        :text-box-label="'検索単語'"
        :text-box-styles="{ width: '320px' }"
        :text-value="searchText"
        @clear-end-date="inputDate('', 'endDate')"
        @clear-start-date="inputDate('', 'startDate')"
        @clear="clearDate"
        @input-end-date="inputDate($event, 'endDate')"
        @input-start-date="inputDate($event, 'startDate')"
        @select-radio-data="changeDisplayType"
        v-model="searchText"
      />
    </div>
    <div class="area list-header">
      <div class="counts">
        <span>検索結果：</span>
        <span class="parentheses">{{ countsText }}</span>
      </div>
      <div class="order-setting">
        <div class="label">並び替え：検査日</div>
        <base-select-box :selectData="orderTypes" v-model="orderType" />
      </div>
    </div>
    <div class="area list">
      <examination-list-table
        v-if="examinations.length > 0"
        :examinations="examinations"
        @click-delete="openDeletePopup"
        @click-edit="openFilePopup"
        @click-pdf="openPdf"
        @click-print="openPrintPopup"
      />
    </div>

    <!-- レイヤーコンポーネント -->
    <announce-popup
      v-if="alertPopup.isOpened"
      data-test="announce-popup alert"
      :buttons="alertPopup.buttons"
      :title="alertPopup.title"
      :type="alertPopup.type"
      @close="closeAlertPopup"
      >{{ alertPopup.message }}</announce-popup
    >
    <announce-popup
      v-if="deletePopup.isOpened"
      data-test="announce-popup delete"
      :buttons="deletePopup.buttons"
      :title="deletePopup.title"
      :type="deletePopup.type"
      @cancel="closeDeletePopup"
      @decision="decideDelete"
      >{{ deletePopup.message }}</announce-popup
    >
    <announce-popup
      v-if="resultPopup.isOpened"
      data-test="announce-popup result"
      :buttons="resultPopup.buttons"
      :title="resultPopup.title"
      :type="resultPopup.type"
      @close="closeResultPopup"
      >{{ resultPopup.message }}</announce-popup
    >
    <base-loading :wait-flg="awaiting" />
    <examination-pdf-file-popup
      v-if="showExaminationPdfFilePopup"
      :encoded-text-file="selectedEncodedTextFile"
      :examination-pdf-file-id="selectedExaminationPdfFileId"
      :patient-id="patientId"
      @close="closeExaminationPdfFilePopup"
      @update="updateFetchedFileMap"
    />
    <print-options-popup
      v-if="showPrintOptionPopup"
      :data-type="'examination'"
      :owner="getOwner(ownerId)"
      :patient="getPatient(patientId)"
      :print-data="printData"
      @close="closePrintOptionPopup"
    />
  </div>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonRegister from '@/components/parts/atoms/BaseButtonRegister'
import BaseLoading from '@/components/parts/atoms/BaseLoading'
import BaseSelectBox from '@/components/parts/atoms/BaseSelectBox'
import ExaminationListTable from '@/components/parts/organisms/ExaminationListTable'
import ExaminationPdfFilePopup from '@/components/popups/ExaminationPdfFilePopup'
import OwnerPatientDetail from '@/components/parts/molecules/OwnerPatientDetail'
import PrintOptionsPopup from '@/components/popups/PrintOptionsPopup'
import SearchArea from '@/components/parts/molecules/SearchArea'
import { PDFDocument } from 'pdf-lib'
import { compareByKeys } from '@/utils/compare'
import { getAxiosObject } from '@/utils/library'
import { groupBy } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'Examinations',

  components: {
    AnnouncePopup,
    BaseButtonRegister,
    BaseLoading,
    BaseSelectBox,
    ExaminationListTable,
    ExaminationPdfFilePopup,
    OwnerPatientDetail,
    PrintOptionsPopup,
    SearchArea
  },

  props: {
    ownerId: { type: Number },
    patientId: { type: Number }
  },

  data() {
    return {
      alertPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        message: 'PDFファイルの取得に失敗しました。',
        title: '注意',
        type: 'alert'
      },
      deletePopup: {
        buttons: ['削除しない', '削除する'],
        isOpened: false,
        message: '削除してもよろしいですか？',
        title: '注意',
        type: 'alert'
      },
      awaiting: false,
      displayType: 1,
      displayTypes: [
        { id: 1, eachValue: 1, labelName: 'すべて' },
        { id: 2, eachValue: 2, labelName: '血液検査のみ' },
        { id: 3, eachValue: 3, labelName: '血液検査以外' }
      ],
      endDate: '',
      fetchedFileMap: new Map(),
      orderType: 0,
      orderTypes: [
        { id: 0, name: '降順' },
        { id: 1, name: '昇順' }
      ],
      printData: {},
      resultPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        message: '',
        title: '',
        type: ''
      },
      searchText: '',
      selectedEncodedTextFile: null,
      selectedExaminationPdfFileId: 0,
      showExaminationPdfFilePopup: false,
      showPrintOptionPopup: false,
      startDate: ''
    }
  },

  computed: {
    ...mapGetters({
      getExaminationPdfFilesByPatientId:
        'examinationPdfFiles/getDataByPatientId',
      getExaminationResultsByPatientId: 'examinationResults/getDataByPatientId',
      getExaminationSpecies: 'examinationSpecies/getDataById',
      getFormat: 'examinationFormats/getDataById',
      getOwner: 'owners/getDataById',
      getPatient: 'patients/getDataById',
      getSpecies: 'species/getDataById',
      getTopic: 'examinationTopics/getDataById',
      getTopicClass: 'examinationTopicClasses/getDataById',
      getTreatment: 'treatments/getDataById'
    }),
    countsText() {
      const all = this.recordCounts
      const filtered = this.filteredRecordCounts
      return `${filtered}件（${all}件中）`
    },
    examinations() {
      return this.formattedPdfFiles
        .concat(this.formattedResultGroups)
        .sort((a, b) => this.compareRows(a, b))
    },
    filteredGroupKeySet() {
      return new Set(this.filteredResults.map(v => v.groupKey))
    },
    filteredPdfFileCounts() {
      return this.filteredPdfFiles.length
    },
    filteredPdfFiles() {
      return this.pdfFiles.filter(v => {
        return (
          this.filterByDisplayType(v) &&
          this.filterByDate(v) &&
          this.filterByFileText(v)
        )
      })
    },
    filteredRecordCounts() {
      return this.filteredPdfFileCounts + this.filteredResultGroupCounts
    },
    filteredResultGroupCounts() {
      return this.filteredGroupKeySet.size
    },
    filteredResults() {
      // 検査結果グループに変換する前に検査結果をフィルタリング
      return this.formattedResults.filter(v => {
        return (
          this.filterByDisplayType(v) &&
          this.filterByDate(v) &&
          this.filterByResultText(v)
        )
      })
    },
    formattedPdfFiles() {
      return this.filteredPdfFiles.map(v => {
        return {
          id: v.id,
          date: v.date,
          examinationName: v.examinationName,
          name: v.name,
          bloodFlg: v.bloodFlg,
          updatedAt: v.updatedAt // リアクティブに更新されているためファイル更新に使用
        }
      })
    },
    formattedResultGroups() {
      // 一覧に使用する検査結果グループ
      const formattedResultGroups = Array.from(this.filteredGroupKeySet).map(
        groupKey => {
          const resultGroup = this.resultGroupByGroupKey[groupKey]
          const resultTopicClasses = this.makeResultTopicClasses(resultGroup)
          const groupOriginalIds = resultGroup.map(v => v.originalId)
          const earliestOriginalId = Math.min(...groupOriginalIds)
          const formattedResultGroup = {
            bloodFlg: resultGroup[0].bloodFlg, // 血液検査
            date: resultGroup[0].date, // 検査日
            earliestOriginalId, // 一番古い値を代表で持たせる 並び替えで使用
            resultTopicClasses, // 項目区分ごとにまとめた検査結果
            speciesName: resultGroup[0].speciesName, // 印刷の種別表記で使用
            treatmentName: resultGroup[0].treatmentName // 検査名
          }
          return formattedResultGroup
        }
      )
      return formattedResultGroups
    },
    formattedResults() {
      // 検査結果を検索と表記に使用するためにフォーマット
      const formattedResults = this.results.map(v => {
        const format = this.getFormat(v.examinationFormatId)
        const treatment = this.getTreatment(v.treatmentId)
        const topicClass = this.getTopicClass(v.examinationTopicClassId)
        const topic = this.getTopic(v.examinationTopicId)
        const examinationSpecies = this.getExaminationSpecies(
          v.examinationSpeciesId
        )
        const species = this.getSpecies(examinationSpecies.speciesId)
        const speciesName = species?.name ?? ''
        const reference = examinationSpecies?.reference ?? ''
        const formattedReference = this.toFormattedReference(
          topic.examinationType,
          reference
        )
        return {
          bloodFlg: format.bloodFlg,
          comment: v.comment,
          date: v.date,
          examinationTopicId: topic.id,
          groupKey: `${v.medicalContentId}-${v.examinationResultGroupId}`,
          id: v.id,
          originalId: v.originalId,
          reference: formattedReference,
          result: v.result,
          speciesName,
          topicClassId: topicClass.id,
          topicClassName: topicClass.name,
          topicName: topic.name,
          treatmentName: treatment.name,
          unit: topic.unit
        }
      })
      return formattedResults
    },
    pdfFileCounts() {
      return this.pdfFiles.length
    },
    pdfFiles() {
      return this.getExaminationPdfFilesByPatientId(this.patientId) ?? []
    },
    recordCounts() {
      return this.resultGroupCounts + this.pdfFileCounts
    },
    resultGroupCounts() {
      return Object.keys(this.resultGroupByGroupKey).length
    },
    resultGroupByGroupKey() {
      return groupBy(this.formattedResults, 'groupKey')
    },
    results() {
      return this.getExaminationResultsByPatientId(this.patientId) ?? []
    },
    trimmedSearchText() {
      return this.searchText.toLowerCase().trim()
    }
  },

  methods: {
    changeDisplayType(value) {
      this.displayType = value
    },
    clearDate() {
      this.startDate = ''
      this.endDate = ''
    },
    closeAlertPopup() {
      this.alertPopup.isOpened = false
    },
    closeDeletePopup() {
      this.deletePopup.isOpened = false
      this.selectedExaminationPdfFileId = 0
    },
    closeExaminationPdfFilePopup() {
      this.showExaminationPdfFilePopup = false
      this.selectedEncodedTextFile = null
      this.selectedExaminationPdfFileId = 0
    },
    closePrintOptionPopup() {
      this.showPrintOptionPopup = false
      this.printData = {}
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
      this.closeDeletePopup()
    },
    compareRows(a, b) {
      // 降順の場合（デフォルト）、
      //   検査結果グループ同士では earliestOriginalId 降順 ※登録順に連番で振られるため
      //   ファイル同士では id 降順 ※登録順に連番で振られるため
      //   検査結果グループとファイルの比較ではファイルを前に並べる
      let sortValue = 0
      sortValue = compareByKeys(a, b, ['date'])
      if (sortValue === 0) {
        if (a.earliestOriginalId && b.earliestOriginalId) {
          sortValue = compareByKeys(a, b, ['earliestOriginalId'])
        } else if (a.earliestOriginalId) {
          sortValue = 1
        } else if (b.earliestOriginalId) {
          sortValue = -1
        } else {
          sortValue = compareByKeys(a, b, ['id'])
        }
      }
      return this.orderType === 1 ? -sortValue : sortValue
    },
    async decideDelete() {
      this.awaiting = true
      const examinationPdfFileId = this.selectedExaminationPdfFileId
      const result = await this.$store.dispatch(
        'examinationPdfFiles/delete',
        examinationPdfFileId
      )
      if (result === 'success') {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除しました',
          title: '完了',
          type: 'success'
        }
        this.deleteFetchedFile(examinationPdfFileId)
      } else {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '削除に失敗しました',
          title: '失敗',
          type: 'failure'
        }
      }
      this.awaiting = false
    },
    deleteFetchedFile(examinationPdfFileId) {
      this.fetchedFileMap.delete(examinationPdfFileId)
    },
    async fetchEncodedTextFile(pdfFile) {
      this.awaiting = true
      try {
        const axiosObject = getAxiosObject()
        const apiUrl = '/examination-pdf-files/file'
        const res = await axiosObject.get(apiUrl, {
          params: { id: pdfFile.id }
        })
        const bufferFile = res.data.file
        const encodedTextFile = Buffer.from(bufferFile, 'base64').toString()
        this.fetchedFileMap.set(pdfFile.id, {
          encodedTextFile,
          updatedAt: pdfFile.updatedAt
        })
        return encodedTextFile
      } catch (error) {
        return null
      } finally {
        this.awaiting = false
      }
    },
    filterByDate(rowItem) {
      const date = rowItem.date
      if (this.startDate !== '' && this.endDate !== '') {
        return this.startDate <= date && date <= this.endDate
      } else if (this.startDate !== '') {
        return this.startDate <= date
      } else if (this.endDate !== '') {
        return date <= this.endDate
      } else {
        return true
      }
    },
    filterByDisplayType(rowItem) {
      const bloodFlg = rowItem.bloodFlg
      if (this.displayType === 2) {
        return !!bloodFlg
      } else if (this.displayType === 3) {
        return !bloodFlg
      } else {
        return true
      }
    },
    filterByFileText(pdfFile) {
      return (
        this.includesText(pdfFile.examinationName) ||
        this.includesText(pdfFile.name)
      )
    },
    filterByGroupText(result) {
      return this.includesText(result.treatmentName)
    },
    filterByTableText(result) {
      const canShow = !!result.result
      return (
        canShow &&
        (this.includesText(result.topicClassName) ||
          this.includesText(result.topicName) ||
          this.includesText(result.comment))
      )
    },
    filterByResultText(result) {
      return this.filterByGroupText(result) || this.filterByTableText(result)
    },
    async findEncodedTextFile(pdfFile) {
      const fetchedFile = this.fetchedFileMap.get(pdfFile.id)
      if (fetchedFile) {
        const isLatest = pdfFile.updatedAt <= fetchedFile.updatedAt
        if (isLatest) {
          return fetchedFile.encodedTextFile
        } else {
          return await this.fetchEncodedTextFile(pdfFile)
        }
      } else {
        return await this.fetchEncodedTextFile(pdfFile)
      }
    },
    includesText(itemText) {
      return itemText.toLowerCase().includes(this.trimmedSearchText)
    },
    inputDate(value, dateType) {
      this[dateType] = value
    },
    makeResultTopicClasses(resultGroup) {
      // 同じ検査結果グループ内で項目区分ごとにまとめる
      const validResultGroup = resultGroup.filter(r => r.result)
      const resultsByTopicClassId = groupBy(validResultGroup, 'topicClassId')
      const resultTopicClasses = Object.entries(resultsByTopicClassId).map(
        ([topicClassId, results]) => {
          return {
            topicClassId: Number(topicClassId),
            topicClassName: results[0].topicClassName,
            resultTopics: results.map(result => ({
              topicName: result.topicName,
              result: result.result,
              unit: result.unit,
              reference: result.reference,
              comment: result.comment
            }))
          }
        }
      )
      return resultTopicClasses
    },
    openDeletePopup(pdfFile) {
      this.selectedExaminationPdfFileId = pdfFile.id
      this.deletePopup.isOpened = true
    },
    async openFilePopup(pdfFile) {
      const encodedTextFile = await this.findEncodedTextFile(pdfFile)
      if (encodedTextFile) {
        this.selectedExaminationPdfFileId = pdfFile.id
        this.selectedEncodedTextFile = encodedTextFile
        this.showExaminationPdfFilePopup = true
      } else {
        this.alertPopup = { ...this.alertPopup, isOpened: true }
      }
    },
    async openPdf(pdfFile) {
      const encodedTextFile = await this.findEncodedTextFile(pdfFile)
      if (encodedTextFile) {
        const pdfDoc = await PDFDocument.load(encodedTextFile)
        pdfDoc.setTitle(pdfFile.name, { showInWindowTitleBar: true })
        const savedPdfBytes = await pdfDoc.save()
        const blob = new Blob([savedPdfBytes], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.setAttribute('id', 'pdf-link')
        link.href = window.URL.createObjectURL(blob)
        window.open(link)
      } else {
        this.alertPopup = { ...this.alertPopup, isOpened: true }
      }
    },
    openPrintPopup(resultGroup) {
      this.printData = { examination: resultGroup }
      this.showPrintOptionPopup = true
    },
    openExaminationPdfFilePopup() {
      this.showExaminationPdfFilePopup = true
    },
    toFormattedReference(examinationType, reference) {
      if (examinationType === '数値') {
        const [min = '', max = ''] = reference.split(',')
        return !!min || !!max ? `${min} – ${max}` : reference
      }
      return reference
    },
    updateFetchedFileMap({ examinationPdfFileId, encodedTextFile, updatedAt }) {
      this.fetchedFileMap.set(examinationPdfFileId, {
        encodedTextFile,
        updatedAt
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.examinations {
  display: inline-block;
  min-width: 100%;
  > .buttons {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 0 20px;
  }
  > .search {
    margin-top: 40px;
  }
  > .list-header {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    > .counts > .parentheses {
      font-feature-settings: 'palt';
    }
    > .order-setting {
      display: flex;
      align-items: center;
      > .label {
        margin-right: 10px;
      }
    }
  }
  > .list {
    min-width: 1175px;
    margin-top: 15px;
    ::v-deep > .examination-list-table > .table-body {
      max-height: 630px;
    }
  }
}
</style>
